// src/components/InvoicePreview.jsx
import React, { useRef, useState } from "react";
import {
  createPdfFromInvoiceElement,
  getPdfBlobFromInvoiceElement,
} from "./logic";
import moment from "moment";
import DOMPurify from "dompurify";
import Swal from "sweetalert2";
import { FaDownload, FaEnvelope, FaPrint } from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import InvoiceSendEmailModal from "./InvoiceSendEmailModal";
import {
  buildInvoiceGstLabel,
} from "../../utilis/applicationCostHelpers";
import {
  DEFAULT_INVOICE_STATUS,
  getInvoiceStatusBadgeClass,
  getInvoiceStatusLabel,
  getInvoiceStatusPdfStyle,
  normalizeInvoiceStatus,
} from "../../utilis/invoiceStatusHelpers";

/** Backend sendInvoiceEmail / getInvoiceEmailHistory: 1 = education, 2 = migration */
const getInvoiceEmailTypeCode = (applicationType) =>
  applicationType === "edu" ? 1 : 2;

const PaperStyles = () => (
  <style jsx global>{`
    #print {
      margin: 0;
      font-family: "Helvetica Neue", "Segoe UI", Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
    }
    #print .sheet {
      margin: 0;
      overflow: hidden;
      position: relative;
      box-sizing: border-box;
      page-break-after: always;
      width: 210mm;
      min-height: 296mm;
      padding: 14mm 16mm;
      background: #fff;
      color: #111;
    }
    #print .sheet.invoice-sheet--pdf {
      overflow: visible;
      min-height: auto;
      height: auto;
      page-break-after: auto;
      padding-bottom: 18mm;
    }
    #print .invoice-amount {
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.02em;
    }
    @media screen {
      #print .sheet {
        margin: 0 auto;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
        border: 1px solid #e5e7eb;
      }
      #print.invoice-preview-wrap {
        background: #f3f4f6;
        padding: 20px 12px;
        border-radius: 8px;
      }
    }
    @media print {
      @page {
        size: A4;
        margin: 0;
      }
      body {
        margin: 0;
      }
      #print.invoice-preview-wrap {
        background: transparent;
        padding: 0;
      }
      #print .sheet {
        box-shadow: none;
        border: none;
      }
      .invoice-action-bar {
        display: none !important;
      }
    }
  `}</style>
);

const InvoicePreview = ({
  clientName,
  clientEmail,
  invoiceNo,
  invoiceDate,
  dueDate,
  reference,
  description,
  currency = "AUD",
  totalCharges,
  payments = [],
  discounts = [],
  paidTotal = 0,
  discountTotal = 0,
  remainingBalance,
  gstInfo = { type: "notax", percentage: 0, amount: 0, baseCost: 0 },
  company = {
    name: "Aptitude Migration Pty Ltd",
    address: "",
    phone: "",
    mobile: "",
    email: "",
    abn: "",
    bsb: "",
    acc: "",
    bankName: "",
  },
  appId,
  applicationType = "mig",
  invoiceStatus = DEFAULT_INVOICE_STATUS,
}) => {
  const printRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState(null);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);

  const fetchEmailHistory = React.useCallback(async () => {
    if (!appId) {
      setEmailHistory(null);
      return;
    }

    const typeCode = getInvoiceEmailTypeCode(applicationType);

    try {
      setLoadingEmailHistory(true);
      const response = await apiUrl.get(
        `getInvoiceEmailHistory/${appId}/${typeCode}`,
      );
      if (response?.data?.status) {
        setEmailHistory(response.data.data || null);
      }
    } catch (error) {
      console.error("Error fetching invoice email history", error);
      setEmailHistory(null);
    } finally {
      setLoadingEmailHistory(false);
    }
  }, [appId, applicationType]);

  React.useEffect(() => {
    fetchEmailHistory();
  }, [fetchEmailHistory]);

  const contentProps = {
    clientName,
    clientEmail,
    invoiceNo,
    invoiceDate,
    dueDate,
    reference,
    description,
    currency,
    totalCharges,
    payments,
    discounts,
    paidTotal,
    discountTotal,
    remainingBalance,
    gstInfo,
    company,
    invoiceStatus: normalizeInvoiceStatus(invoiceStatus),
  };

  const handleDownload = () => {
    if (printRef.current) {
      createPdfFromInvoiceElement(printRef.current);
    }
  };

  const handleSendEmail = async () => {
    if (!clientEmail) {
      return Swal.fire(
        "Missing Email",
        "This client does not have an email address on file.",
        "warning",
      );
    }
    if (!appId) {
      return Swal.fire("Error", "Application is not selected.", "error");
    }
    if (!invoiceNo) {
      return Swal.fire("Error", "Invoice number is not available.", "error");
    }
    if (!printRef.current) {
      return Swal.fire("Error", "Invoice preview is not ready.", "error");
    }

    const pdfFilename = `INV-${invoiceNo}.pdf`;
    const emailSubject = `Invoice INV-${invoiceNo} from Aptitude Migration`;
    const emailBody = `Dear ${clientName || "Client"},\n\nPlease find attached invoice INV-${invoiceNo} for ${currency} ${Number(remainingBalance || totalCharges || 0).toFixed(2)}.\n\nKindly acknowledge this invoice. If you have any questions, please let us know.\n\nBest regards,\nAptitude Migration Pty Ltd`;

    try {
      setSendingEmail(true);

      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      const pdfBlob = await getPdfBlobFromInvoiceElement(printRef.current);

      const formData = new FormData();
      formData.append("appid", String(appId));
      formData.append("type", String(getInvoiceEmailTypeCode(applicationType)));
      formData.append("email", clientEmail);
      formData.append("invoice_no", invoiceNo);
      formData.append("client_name", clientName || "");
      formData.append("description", description || "");
      formData.append("subject", emailSubject);
      formData.append("message", emailBody);
      formData.append("pdf", pdfBlob, pdfFilename);

      const response = await apiUrl.post("sendInvoiceEmail", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!response?.data?.status) {
        throw new Error(
          response?.data?.message || "Failed to send invoice email.",
        );
      }

      Swal.fire(
        "Sent!",
        response?.data?.message ||
          "Invoice PDF emailed to client successfully.",
        "success",
      );
      setShowEmailModal(false);
      await fetchEmailHistory();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send invoice email.",
        "error",
      );
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
      <PaperStyles />
      <div className="invoice-preview-status-banner">
        <span className="invoice-preview-status-banner__label">Status</span>
        <span
          className={`invoice-preview-status-banner__badge ${getInvoiceStatusBadgeClass(
            contentProps.invoiceStatus,
          )}`}
        >
          {getInvoiceStatusLabel(contentProps.invoiceStatus)}
        </span>
      </div>
      <div id="print" className="invoice-preview-wrap">
        <InvoiceContent {...contentProps} />

        <div
          aria-hidden="true"
          className="invoice-pdf-capture"
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: -1,
            opacity: 0,
            pointerEvents: "none",
            width: "210mm",
            background: "#fff",
          }}
        >
          <InvoiceContent {...contentProps} forPdf innerRef={printRef} />
        </div>
      </div>

      <div className="invoice-action-bar d-flex justify-content-center flex-wrap gap-2 mt-3 pt-2">
        <button
          type="button"
          className="btn btn-outline-dark btn-sm px-3 d-inline-flex align-items-center gap-2"
          onClick={() => window.print()}
        >
          <FaPrint size={13} /> Print
        </button>
        <button
          type="button"
          className="btn btn-dark btn-sm px-3 d-inline-flex align-items-center gap-2"
          onClick={handleDownload}
        >
          <FaDownload size={13} /> Download PDF
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center gap-2"
          onClick={() => setShowEmailModal(true)}
          disabled={!clientEmail}
          title={
            clientEmail
              ? "Email invoice PDF to client"
              : "Client email is required"
          }
        >
          <FaEnvelope size={13} /> Email to Client
        </button>
      </div>

      <div className="invoice-email-history">
        <div className="invoice-email-history__title">Email Delivery Log</div>
        {loadingEmailHistory ? (
          <div className="invoice-email-history__text text-muted">
            Loading email history...
          </div>
        ) : Number(emailHistory?.send_count || 0) > 0 ? (
          <div className="invoice-email-history__grid">
            <div>
              <span className="invoice-email-history__label">Sent to</span>
              <span className="invoice-email-history__value">
                {emailHistory?.email || clientEmail || "—"}
              </span>
            </div>
            <div>
              <span className="invoice-email-history__label">Times sent</span>
              <span className="invoice-email-history__value">
                {emailHistory?.send_count || 0}
              </span>
            </div>
            <div>
              <span className="invoice-email-history__label">Last sent</span>
              <span className="invoice-email-history__value">
                {emailHistory?.last_sent_at
                  ? moment(emailHistory.last_sent_at).format(
                      "D MMM YYYY, h:mm A",
                    )
                  : "—"}
              </span>
            </div>
            {emailHistory?.invoice_no && (
              <div>
                <span className="invoice-email-history__label">Invoice No.</span>
                <span className="invoice-email-history__value">
                  INV-{emailHistory.invoice_no}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="invoice-email-history__text text-muted">
            This invoice has not been emailed to the client yet.
          </div>
        )}
      </div>

      <InvoiceSendEmailModal
        show={showEmailModal}
        onClose={() => !sendingEmail && setShowEmailModal(false)}
        onSend={handleSendEmail}
        sending={sendingEmail}
        clientName={clientName}
        clientEmail={clientEmail}
        invoiceNo={invoiceNo}
      />
    </>
  );
};

const MetaField = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#6b7280",
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#111", lineHeight: 1.4 }}>{value}</div>
    </div>
  );
};

const buildGstTotalLabel = ({
  gstInfo,
  gstPercentage,
  gstAmount,
  subtotal,
  totalAmount,
}) =>
  buildInvoiceGstLabel({
    gstInfo,
    gstPercentage,
    gstAmount,
    subtotal,
    totalAmount,
  });

const TotalRow = ({ label, value, bold, highlight }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      padding: bold ? "10px 0" : "7px 0",
      borderTop: highlight ? "2px solid #111" : bold ? "1px solid #d1d5db" : "none",
      marginTop: highlight ? 6 : 0,
      fontWeight: bold || highlight ? 700 : 400,
      fontSize: highlight ? 14 : 13,
      color: "#111",
    }}
  >
    <span
      style={{
        letterSpacing: highlight ? "0.04em" : "normal",
        flex: "1 1 auto",
        minWidth: 0,
        paddingRight: 12,
        whiteSpace: "normal",
        lineHeight: 1.35,
      }}
    >
      {label}
    </span>
    <span
      className="invoice-amount"
      style={{
        textAlign: "right",
        minWidth: 90,
        flexShrink: 0,
      }}
    >
      {value}
    </span>
  </div>
);

const FooterInfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      fontSize: 13,
      color: "#374151",
      padding: "5px 0",
      borderBottom: "1px dashed #e5e7eb",
    }}
  >
    <span style={{ color: "#6b7280", fontWeight: 500 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#111", textAlign: "right" }}>
      {value}
    </span>
  </div>
);

const InvoiceContent = ({
  clientName,
  clientEmail,
  invoiceNo,
  invoiceDate,
  dueDate,
  reference,
  description,
  currency,
  totalCharges,
  payments,
  discounts,
  paidTotal = 0,
  discountTotal = 0,
  remainingBalance,
  gstInfo = { type: "notax", percentage: 0, amount: 0, baseCost: 0 },
  company,
  forPdf = false,
  innerRef = null,
  invoiceStatus = DEFAULT_INVOICE_STATUS,
}) => {
  const normalizedStatus = normalizeInvoiceStatus(invoiceStatus);
  const statusLabel = getInvoiceStatusLabel(normalizedStatus);
  const statusPdfStyle = getInvoiceStatusPdfStyle(normalizedStatus);
  const toNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const normalized = value.replace(/,/g, "").trim();
      if (!normalized) return 0;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const fmt = (n) => toNumber(n).toFixed(2);
  const gstAmount = toNumber(gstInfo?.amount);
  const gstPercentage = Number(gstInfo?.percentage || 0);
  const gstType = gstInfo?.type || "notax";
  const baseCost = toNumber(gstInfo?.baseCost);
  const showGst = gstAmount > 0;

  const subtotal =
    baseCost > 0
      ? baseCost
      : showGst
        ? toNumber(totalCharges) - gstAmount
        : toNumber(totalCharges);

  const totalAmount = toNumber(totalCharges);
  const totalPaidFromEntries = payments.reduce(
    (sum, p) => sum + toNumber(p.amount),
    0,
  );
  const totalDiscountFromEntries = discounts.reduce(
    (sum, d) => sum + toNumber(d.amount),
    0,
  );
  const totalPaid = Math.max(totalPaidFromEntries, toNumber(paidTotal));
  const totalDiscount = Math.max(
    totalDiscountFromEntries,
    toNumber(discountTotal),
  );
  const amountDue = toNumber(remainingBalance);

  const sectionLabelStyle = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#6b7280",
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: "1px solid #e5e7eb",
  };

  const footerBoxStyle = {
    background: "#fafafa",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "16px 18px",
    height: "100%",
  };

  return (
    <div
      ref={innerRef}
      className={`sheet${forPdf ? " invoice-sheet--pdf" : ""}`}
      style={
        forPdf
          ? {
              boxShadow: "none",
              border: "none",
              margin: 0,
              width: "210mm",
            }
          : undefined
      }
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 28,
          paddingBottom: 20,
          borderBottom: "2px solid #111",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: "#111",
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Invoice
            </h1>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "8px 16px",
                borderRadius: 6,
                border: `2px solid ${statusPdfStyle.border}`,
                background: statusPdfStyle.background,
                color: statusPdfStyle.color,
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {statusLabel}
            </span>
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#6b7280",
              marginTop: 6,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Tax Invoice
          </div>
        </div>
        <img
          src={`${process.env.PUBLIC_URL}/assets/img/c_logo.jpg`}
          alt="Company Logo"
          className="h-12 object-contain"
        />
      </div>

      {/* Three-column info */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 24,
          marginBottom: 28,
        }}
      >
        <div>
          <div style={sectionLabelStyle}>Bill To</div>
          {clientName && (
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 4 }}>
              {clientName}
            </div>
          )}
          {clientEmail && (
            <div style={{ fontSize: 13, color: "#374151" }}>{clientEmail}</div>
          )}
        </div>

        <div>
          <div style={sectionLabelStyle}>Invoice Details</div>
          <MetaField
            label="Invoice Date"
            value={moment(invoiceDate).format("D MMMM YYYY")}
          />
          <MetaField label="Invoice No." value={`INV-${invoiceNo}`} />
          <MetaField label="Status" value={statusLabel} />
          <MetaField label="Reference" value={reference || "—"} />
          {company.abn && <MetaField label="ABN" value={company.abn} />}
        </div>

        <div>
          <div style={sectionLabelStyle}>From</div>
          {company.name && (
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 6 }}>
              {company.name}
            </div>
          )}
          {company.address && (
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>
              {company.address}
            </div>
          )}
          {company.phone && (
            <div style={{ fontSize: 13, color: "#374151" }}>P: {company.phone}</div>
          )}
          {company.mobile && (
            <div style={{ fontSize: 13, color: "#374151" }}>M: {company.mobile}</div>
          )}
          {company.email && (
            <div style={{ fontSize: 13, color: "#374151" }}>E: {company.email}</div>
          )}
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: 32 }}>
        <div style={sectionLabelStyle}>Description</div>
        <div
          style={{
            fontSize: 13,
            color: "#111",
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
            padding: "12px 14px",
            background: "#fafafa",
            border: "1px solid #e5e7eb",
            borderRadius: 4,
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(description || ""),
          }}
        />
      </div>

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 32 }}>
        <div
          style={{
            width: "100%",
            maxWidth: 340,
            border: "1px solid #e5e7eb",
            borderRadius: 4,
            padding: "12px 18px",
            background: "#fafafa",
          }}
        >
          <TotalRow label="Subtotal" value={fmt(subtotal)} />
          <TotalRow
            label={buildGstTotalLabel({
              gstInfo,
              gstPercentage,
              gstAmount,
              subtotal,
              totalAmount,
            })}
            value={fmt(showGst ? gstAmount : 0)}
          />
          <TotalRow label={`TOTAL ${currency}`} value={fmt(totalAmount)} bold />
          {totalPaid > 0 && (
            <TotalRow label="Less Amount Paid" value={fmt(totalPaid)} />
          )}
          {totalDiscount > 0 && (
            <TotalRow label="Less Discount" value={fmt(totalDiscount)} />
          )}
          <TotalRow
            label={`AMOUNT DUE ${currency}`}
            value={fmt(amountDue)}
            highlight
          />
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          paddingTop: 20,
          borderTop: "2px solid #111",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginBottom: 16,
          }}
        >
          <div style={footerBoxStyle}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#6b7280",
                marginBottom: 12,
              }}
            >
              Payment Due
            </div>
            <FooterInfoRow
              label="Due Date"
              value={moment(dueDate).format("D MMMM YYYY")}
            />
            <FooterInfoRow
              label="Amount Due"
              value={`${currency} ${fmt(amountDue)}`}
            />
            {reference && (
              <FooterInfoRow label="Payment Reference" value={reference} />
            )}
            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                color: "#6b7280",
                lineHeight: 1.5,
              }}
            >
              Please include the invoice number{" "}
              <strong>INV-{invoiceNo}</strong> with your payment.
            </div>
          </div>

          <div style={footerBoxStyle}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#6b7280",
                marginBottom: 12,
              }}
            >
              Our Banking Details
            </div>
            {company.b_account_name && (
              <FooterInfoRow
                label="Account Name"
                value={company.b_account_name}
              />
            )}
            {company.bsb && <FooterInfoRow label="BSB" value={company.bsb} />}
            {company.acc && (
              <FooterInfoRow label="Account" value={company.acc} />
            )}
            {(company.bankName || company.b_name) && (
              <FooterInfoRow
                label="Bank"
                value={company.bankName || company.b_name}
              />
            )}
            {!company.b_account_name &&
              !company.bsb &&
              !company.acc &&
              !company.bankName &&
              !company.b_name && (
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  Banking details are not configured.
                </div>
              )}
          </div>
        </div>

        <div
          style={{
            textAlign: "center",
            fontSize: 12,
            color: "#6b7280",
            paddingTop: 12,
            paddingBottom: forPdf ? 8 : 0,
            lineHeight: 1.6,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          Thank you for your business. For any queries regarding this invoice,
          please contact {company.email || "info@aptitudemigration.com.au"}.
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
